import { useEffect, useMemo } from 'react';
import { ArrowLeft, Printer } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  buildAdminShippingLabelViewModel,
  getAdminShippingLabelOrder,
  mapAdminShippingLabelError,
  useTranslation,
} from '@dukanti/shared';
import type { Store } from '@dukanti/shared';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { ShippingLabelPreview } from '@/features/orders/components';
import { useStore } from '@/features/store-settings/use-store';
import { supabase } from '@/lib/supabase';

function ShippingLabelLoading(): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <main
      data-testid="shipping-label-loading"
      aria-label={t('admin.orders.shippingLabel.loading')}
      className="bg-background min-h-screen px-4 py-6 sm:px-6 lg:px-8"
    >
      <div className="mx-auto flex w-full max-w-5xl flex-col gap-6">
        <p className="text-muted-foreground text-sm">{t('admin.orders.shippingLabel.loading')}</p>
        <Skeleton className="h-10 w-48 rounded-lg" />
        <Skeleton className="mx-auto h-[150mm] w-[100mm] max-w-full rounded-lg" />
      </div>
    </main>
  );
}

interface ShippingLabelErrorProps {
  readonly onBack: () => void;
  readonly onRetry: () => void;
}

function ShippingLabelError({ onBack, onRetry }: ShippingLabelErrorProps): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <main
      data-testid="shipping-label-error"
      className="bg-background flex min-h-screen items-start justify-center px-6 py-16"
    >
      <section className="border-border bg-card shadow-admin-card flex max-w-md flex-col items-center gap-4 rounded-lg border p-8 text-center">
        <h1 className="font-display text-foreground text-xl font-semibold">
          {t('admin.orders.shippingLabel.notFoundTitle')}
        </h1>
        <p className="text-muted-foreground text-sm">
          {t('admin.orders.shippingLabel.notFoundDescription')}
        </p>
        <div className="mt-2 flex flex-wrap justify-center gap-3">
          <Button type="button" variant="default" onClick={onBack}>
            {t('admin.orders.shippingLabel.backToOrder')}
          </Button>
          <Button type="button" variant="ghost" onClick={onRetry}>
            {t('admin.orders.shippingLabel.retry')}
          </Button>
        </div>
      </section>
    </main>
  );
}

function ShippingLabelBlocked({
  onBack,
  deliveryOnly = false,
}: Pick<ShippingLabelErrorProps, 'onBack'> & {
  readonly deliveryOnly?: boolean;
}): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <main
      data-testid="shipping-label-blocked"
      className="bg-background flex min-h-screen items-start justify-center px-6 py-16"
    >
      <section className="border-honey/40 bg-card shadow-admin-card flex max-w-md flex-col items-center gap-4 rounded-lg border p-8 text-center">
        <h1 className="font-display text-foreground text-xl font-semibold">
          {t(
            deliveryOnly
              ? 'admin.orders.fulfillment.deliveryOnly'
              : 'admin.orders.shippingLabel.manualApprovalBlockedTitle',
          )}
        </h1>
        <p className="text-muted-foreground text-sm">
          {t(
            deliveryOnly
              ? 'admin.orders.fulfillment.deliveryOnly'
              : 'admin.orders.shippingLabel.manualApprovalBlockedDescription',
          )}
        </p>
        <Button type="button" variant="default" onClick={onBack}>
          {t('admin.orders.shippingLabel.backToOrder')}
        </Button>
      </section>
    </main>
  );
}

export function OrderShippingLabelRoute(): React.JSX.Element {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const { t, dir, lang } = useTranslation();
  const orderQuery = useQuery({
    queryKey: ['admin-shipping-label-order', orderId],
    enabled: !!orderId,
    queryFn: () => getAdminShippingLabelOrder(supabase, orderId as string),
    retry: false,
  });
  const senderStoreId = orderQuery.data?.store_id ?? null;
  const storeQuery = useStore(senderStoreId);
  const printedAt = useMemo(() => (orderQuery.data?.id ? new Date() : null), [orderQuery.data?.id]);

  useEffect(() => {
    document.body.classList.add('shipping-label-print-mode');
    return () => {
      document.body.classList.remove('shipping-label-print-mode');
    };
  }, []);

  function handleBack(): void {
    navigate(orderId ? `/orders/${orderId}` : '/orders');
  }

  function handleRetry(): void {
    void orderQuery.refetch();
    if (senderStoreId) void storeQuery.refetch();
  }

  function handlePrint(): void {
    window.print?.();
  }

  if (orderQuery.isLoading || (orderQuery.data && storeQuery.isLoading)) {
    return <ShippingLabelLoading />;
  }

  if (
    orderQuery.isError &&
    mapAdminShippingLabelError(orderQuery.error) === 'order.errors.manualApprovalRequired'
  ) {
    return <ShippingLabelBlocked onBack={handleBack} />;
  }

  if (
    (orderQuery.isError &&
      mapAdminShippingLabelError(orderQuery.error) === 'admin.orders.fulfillment.deliveryOnly') ||
    orderQuery.data?.delivery_method === 'pickup'
  ) {
    return <ShippingLabelBlocked onBack={handleBack} deliveryOnly />;
  }

  if (orderQuery.isError || !orderQuery.data || !printedAt) {
    return <ShippingLabelError onBack={handleBack} onRetry={handleRetry} />;
  }

  const orderStore = orderQuery.data.store_id;
  const senderStore: Store | null = storeQuery.data?.id === orderStore ? storeQuery.data : null;
  const viewModel = buildAdminShippingLabelViewModel({
    order: orderQuery.data,
    courierAssignment: orderQuery.data.courier_assignment ?? null,
    store: senderStore,
    locale: lang,
    printedAt,
  });

  return (
    <main
      dir={dir}
      data-testid="shipping-label-route"
      className="bg-background min-h-screen px-4 py-6 sm:px-6 lg:px-8"
    >
      <div className="mx-auto flex w-full max-w-5xl flex-col gap-6">
        <div className="print:hidden">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <Button
              type="button"
              variant="ghost"
              onClick={handleBack}
              aria-label={t('admin.orders.shippingLabel.backToOrder')}
            >
              <ArrowLeft aria-hidden="true" className="size-4 rtl:rotate-180" strokeWidth={1.75} />
              <span>{t('admin.orders.shippingLabel.backToOrder')}</span>
            </Button>
            <Button
              type="button"
              variant="default"
              onClick={handlePrint}
              title={t('admin.orders.shippingLabel.printAria')}
              data-testid="shipping-label-print-action"
            >
              <Printer aria-hidden="true" className="size-4" strokeWidth={1.75} />
              <span>{t('admin.orders.shippingLabel.print')}</span>
            </Button>
          </div>
          <h1 className="font-display text-foreground mt-4 text-start text-2xl font-semibold">
            {t('admin.orders.shippingLabel.previewHeading')}
          </h1>
        </div>

        <ShippingLabelPreview viewModel={viewModel} dir={dir} />
      </div>
    </main>
  );
}
